// /app/brands/$brandId/calendar — month-grid view of every scheduled post.
//
// Why this exists: the bulk-generation slice creates 8-30 stub posts
// scheduled across a date range. Without a calendar the user sees only
// a flat list and can't spot gaps in cadence, weekday vs weekend
// balance, or platform mix on a given day.
//
// Data sources (v0.1.x, all client-side):
//   - GET /v1/brands/{id}/posts          — every post (any status)
//   - GET /v1/brands/{id}/bulk-jobs      — pending stub generations
//     with scheduled_for set
//
// We fetch both and bucket items by ISO date. Server-side date-range
// filtering (spec docs/05-api §7.1) is deferred until a brand has so
// many posts that client-side fetching+bucketing gets slow — far
// beyond v0.1.x scale.

import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/Button";
import { useI18n } from "../../../lib/i18n";
import {
  ApiError,
  brands as brandsAPI,
  bulkGenerations,
  posts as postsAPI,
  type BulkJob,
  type Post,
} from "../../../lib/api";

// CalendarItem is the union of "a scheduled post" + "a stub generation
// from a bulk job". We render both so the user sees the full plan,
// distinguished by `kind`.
type CalendarItem =
  | { kind: "post"; date: Date; post: Post }
  | { kind: "stub"; date: Date; bulkJobId: string; generationId: string; platform: string };

function BrandCalendar() {
  const { t, locale } = useI18n();
  const nav = useNavigate();
  const { brandId } = Route.useParams();

  // The month being viewed. Defaults to current; nav arrows shift by ±1.
  const [cursor, setCursor] = useState<Date>(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const [posts, setPosts] = useState<Post[] | null>(null);
  const [bulkJobs, setBulkJobs] = useState<BulkJob[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Brand header (just trade_name) for the page title. Light fetch.
  const [brandName, setBrandName] = useState<string>("");
  useEffect(() => {
    brandsAPI.get(brandId).then((b) => setBrandName(b.trade_name)).catch(() => { /* ignore */ });
  }, [brandId]);

  // Bulk-load posts + bulk-jobs once per brand. The view filters
  // client-side as the user pages months.
  useEffect(() => {
    let cancelled = false;
    setErr(null);
    Promise.all([
      postsAPI.listForBrand(brandId).then((r) => r.posts),
      bulkGenerations.listForBrand(brandId, 100),
    ])
      .then(([ps, bjr]) => {
        if (cancelled) return;
        setPosts(ps);
        setBulkJobs(bjr.bulk_jobs);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e instanceof ApiError ? e.message : t("common.errorGeneric"));
      });
    return () => { cancelled = true; };
  }, [brandId, t]);

  // Flatten to CalendarItems whenever data changes. Each bulk-job's
  // children are nested in the BulkJob payload only when GetBulkJob is
  // called — listForBrand returns only the parents. So for the listing
  // pass we need to fetch each job's children. That's wasteful for
  // large numbers; in practice an SMB brand has 0-5 active bulk jobs.
  // Lazy-load on demand: only fetch children for jobs that overlap the
  // currently-displayed month.
  const [bulkChildren, setBulkChildren] = useState<Record<string, BulkJob>>({});
  useEffect(() => {
    if (!bulkJobs) return;
    const monthStart = startOfMonth(cursor);
    const monthEnd = endOfMonth(cursor);
    const relevant = bulkJobs.filter((j) => {
      const jStart = new Date(j.start_date);
      const jEnd = new Date(j.end_date);
      return jEnd >= monthStart && jStart <= monthEnd;
    });
    let cancelled = false;
    for (const j of relevant) {
      if (bulkChildren[j.id]) continue; // already fetched
      bulkGenerations.get(j.id).then((full) => {
        if (cancelled) return;
        setBulkChildren((prev) => ({ ...prev, [j.id]: full }));
      }).catch(() => { /* ignore single-job failure */ });
    }
    return () => { cancelled = true; };
    // We intentionally don't list bulkChildren in deps to avoid loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkJobs, cursor]);

  const items = useMemo<CalendarItem[]>(() => {
    const out: CalendarItem[] = [];
    if (posts) {
      for (const p of posts) {
        if (!p.scheduled_for) continue;
        out.push({ kind: "post", date: new Date(p.scheduled_for), post: p });
      }
    }
    for (const j of Object.values(bulkChildren)) {
      for (const c of j.children ?? []) {
        // A bulk-job child becomes a `post` once the user runs AI on
        // it + approves. While it's still in generation_versions land
        // with status=pending|running|ready|failed, we render it as
        // a "stub" with a yellow tone to distinguish from real posts.
        if (c.status === "approved") continue;
        if (!c.scheduled_for) continue;
        out.push({
          kind: "stub",
          date: new Date(c.scheduled_for),
          bulkJobId: j.id,
          generationId: c.generation_id,
          platform: c.platform,
        });
      }
    }
    return out;
  }, [posts, bulkChildren]);

  // Bucket items by yyyy-mm-dd for fast cell lookup.
  const itemsByDay = useMemo(() => {
    const m = new Map<string, CalendarItem[]>();
    for (const it of items) {
      const key = isoDay(it.date);
      const arr = m.get(key) ?? [];
      arr.push(it);
      m.set(key, arr);
    }
    return m;
  }, [items]);

  const monthDays = useMemo(() => buildMonthGrid(cursor), [cursor]);

  if (err) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12">
        <Card><p className="text-error">{err}</p></Card>
      </div>
    );
  }
  if (posts === null || bulkJobs === null) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12">
        <Card><p className="text-secondary">{t("common.loading")}</p></Card>
      </div>
    );
  }

  const monthLabel = cursor.toLocaleString(locale === "tr" ? "tr-TR" : "en-US",
    { month: "long", year: "numeric" });

  // onCellClick: if the day already has items, do nothing (user
  // should click a specific pill). If empty + in the current/future
  // months, navigate to the composer pre-filled with brand_id; the
  // composer will eventually accept a `scheduled_for` param and pin
  // it on submit (next slice). For now the date itself is hinted
  // via the URL fragment so the composer can pick it up later.
  function handleCellClick(date: Date, hasItems: boolean) {
    if (hasItems) return;
    if (date < new Date(new Date().setHours(0, 0, 0, 0))) return; // past dates: ignore
    void nav({
      to: "/app/content/new",
      search: { brand_id: brandId },
    });
  }

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-2 min-w-0">
          <Link to="/app/brands/$brandId" params={{ brandId }}
                className="text-xs text-muted hover:text-primary ring-nexus rounded inline-block w-fit">
            ← {brandName || t("brands.title")}
          </Link>
          <h1 className="font-display text-3xl font-semibold">{t("calendar.title")}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCursor(addMonths(cursor, -1))}
            className="ring-nexus w-9 h-9 rounded-[10px] bg-elevated border border-border-default text-secondary hover:text-primary hover:border-border-strong transition flex items-center justify-center"
            aria-label={t("calendar.prev")}
          >‹</button>
          <button
            type="button"
            onClick={() => setCursor(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}
            className="ring-nexus rounded-[10px] bg-elevated border border-border-default px-3 py-1.5 text-sm text-secondary hover:text-primary transition font-mono"
          >
            {locale === "tr" ? "Bugün" : "Today"}
          </button>
          <span className="px-3 py-1 font-display text-sm font-semibold capitalize min-w-[140px] text-center">
            {monthLabel}
          </span>
          <button
            type="button"
            onClick={() => setCursor(addMonths(cursor, 1))}
            className="ring-nexus w-9 h-9 rounded-[10px] bg-elevated border border-border-default text-secondary hover:text-primary hover:border-border-strong transition flex items-center justify-center"
            aria-label={t("calendar.next")}
          >›</button>
          <Link to="/app/content/new" search={{ brand_id: brandId }}>
            <Button variant="primary">+ {t("brands.new")}</Button>
          </Link>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-secondary flex-wrap">
        <Legend tone="bg-success" label={t("calendar.legend.published")} />
        <Legend tone="bg-info" label={t("calendar.legend.scheduled")} />
        <Legend tone="bg-warning" label={t("calendar.legend.draft")} />
        <Legend tone="bg-muted" label={t("calendar.legend.stub")} />
        <Legend tone="bg-error" label={t("calendar.legend.failed")} />
      </div>

      {/* Grid */}
      <div className="border border-border-default rounded-[14px] overflow-hidden bg-surface">
        {/* Day-of-week header */}
        <div className="grid grid-cols-7 bg-elevated text-[11px] font-mono uppercase tracking-[0.16em] text-secondary">
          {dayOfWeekLabels(locale).map((d) => (
            <div key={d} className="px-3 py-2.5 text-center border-r last:border-r-0 border-border-subtle">{d}</div>
          ))}
        </div>
        {/* Cells */}
        <div className="grid grid-cols-7">
          {monthDays.map((day, i) => {
            const cellKey = isoDay(day.date);
            const inMonth = day.inMonth;
            const dayItems = itemsByDay.get(cellKey) ?? [];
            const isToday = isSameDay(day.date, new Date());
            const past = day.date < new Date(new Date().setHours(0, 0, 0, 0));
            const hoverable = inMonth && !past && dayItems.length === 0;
            return (
              <div
                key={i}
                onClick={() => handleCellClick(day.date, dayItems.length > 0)}
                className={[
                  "group relative min-h-[120px] border-r border-b border-border-subtle p-2 flex flex-col gap-1 transition",
                  inMonth ? "bg-surface" : "bg-shell/50",
                  !inMonth ? "opacity-50" : "",
                  isToday ? "ring-1 ring-inset ring-red" : "",
                  hoverable ? "cursor-pointer hover:bg-elevated" : "",
                ].join(" ")}
              >
                <span className={[
                  "text-[12px] font-mono shrink-0",
                  isToday ? "text-red font-bold"
                    : inMonth ? "text-secondary"
                    : "text-muted",
                ].join(" ")}>
                  {day.date.getDate()}
                </span>
                {dayItems.map((it, idx) => (
                  <CalendarPill key={idx} item={it} brandId={brandId} />
                ))}
                {hoverable ? (
                  <span className="absolute inset-x-0 bottom-1 text-center text-[10px] font-mono uppercase tracking-wider text-muted opacity-0 group-hover:opacity-100 transition pointer-events-none">
                    + {locale === "tr" ? "Yeni içerik" : "New post"}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {items.length === 0 ? (
        <Card>
          <p className="text-secondary text-sm">{t("calendar.empty.title")}</p>
          <p className="text-xs text-muted mt-2">{t("calendar.empty.hint")}</p>
        </Card>
      ) : null}
    </div>
  );
}

// CalendarPill — single bar in a cell. Colour-coded by post status,
// with platform name + scheduled time. Clicking navigates to the
// right detail page (post or generation). stopPropagation so the
// enclosing cell's click handler doesn't also fire and navigate to
// /app/content/new.
function CalendarPill({ item, brandId }: { item: CalendarItem; brandId: string }) {
  const timeLabel = item.date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (item.kind === "post") {
    const p = item.post;
    const tone =
      p.status === "published" ? "bg-success/15 text-success border-success/40 hover:bg-success/25"
        : p.status === "failed" ? "bg-error/15 text-error border-error/40 hover:bg-error/25"
        : p.status === "scheduled" ? "bg-info/15 text-info border-info/40 hover:bg-info/25"
        : "bg-warning/15 text-warning border-warning/40 hover:bg-warning/25";
    return (
      <Link
        to="/app/posts/$postId"
        params={{ postId: p.id }}
        onClick={(e) => e.stopPropagation()}
        className={`rounded-md border px-1.5 py-1 text-[10px] font-mono truncate transition ${tone}`}
        title={`${p.platform} · ${p.status} · ${timeLabel}${p.text_body ? "\n" + p.text_body.slice(0, 80) : ""}`}
      >
        <span className="opacity-70 mr-1">{timeLabel}</span>
        {p.platform}
      </Link>
    );
  }
  // Stub generation from a bulk job — no post row yet.
  void brandId; // reserved for future "open composer pre-filled for this stub"
  return (
    <Link
      to="/app/generations/$generationId"
      params={{ generationId: item.generationId }}
      onClick={(e) => e.stopPropagation()}
      className="rounded-md border px-1.5 py-1 text-[10px] font-mono truncate bg-muted/15 text-muted border-muted/40 hover:bg-muted/25 transition italic"
      title={`${item.platform} · stub from bulk job · ${timeLabel}`}
    >
      <span className="opacity-70 mr-1">{timeLabel}</span>
      {item.platform} ·
    </Link>
  );
}

function Legend({ tone, label }: { tone: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block w-3 h-3 rounded ${tone}`} aria-hidden />
      <span>{label}</span>
    </span>
  );
}

// ---------- date helpers ----------

function startOfMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d: Date)   { return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59); }
function addMonths(d: Date, n: number) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function isoDay(d: Date) {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}
function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// Builds the 42-cell grid for a given month. Sundays as last column
// would be customary in TR; we go Monday-first (locale-neutral default).
function buildMonthGrid(monthCursor: Date) {
  const first = startOfMonth(monthCursor);
  // 0 = Sunday in JS; we want Monday-first, so shift.
  const firstDow = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(start.getDate() - firstDow);
  const cells: { date: Date; inMonth: boolean }[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push({ date: d, inMonth: d.getMonth() === monthCursor.getMonth() });
  }
  return cells;
}

function dayOfWeekLabels(locale: "tr" | "en") {
  const fmt = new Intl.DateTimeFormat(locale === "tr" ? "tr-TR" : "en-US", { weekday: "short" });
  // Pick Monday=2026-05-04 as a reference; iterate 7 days.
  const ref = new Date(2026, 4, 4);
  const out: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(ref);
    d.setDate(ref.getDate() + i);
    out.push(fmt.format(d));
  }
  return out;
}

export const Route = createFileRoute("/app/brands/$brandId_/calendar")({
  component: BrandCalendar,
});
